import { Formik } from 'formik';
import { useCreateCompany } from 'generated/api-client/@tanstack/react-query.gen';
import { CompanyForm } from 'modules/company/components/CompanyForm';
import { CountryCode } from 'modules/company/types/company.type';
import { CompanyFormFields } from 'modules/company/types/companyFormFields.type';
import { companyFormValidationSchema } from 'modules/company/utils/companyFormValidationSchema';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { ToastError, ToastSuccess } from 'utils';
import { CompanyFormModalProps } from '../../../components/company-form-modal/company-form-modal';

type Props = CompanyFormModalProps & {
  onClose: () => void;
  onCompanyCreated?: (companyId: number) => void;
};

export const CompanyCreateForm: FunctionComponent<Props> = ({
  onCompanyCreated,
  onClose,
  ...rest
}) => {
  const { mutateAsync: createCompany } = useCreateCompany({
    onSuccess: (data) => {
      onCompanyCreated?.(data.id);
      ToastSuccess('Succès', 'Entreprise créée');
    },
    onError: () => {
      ToastError('Erreur', "Impossible de créer l'entreprise");
    },
  });

  const submit = useCallback(
    async (values: CompanyFormFields) => {
      await createCompany({
        body: {
          name: values.name.trim(),
          countryCode: values.countryCode,
          registrationNumber: values.registrationNumber.trim(),
          legalForm: values.legalForm.trim(),
          vatNumber: values.vatNumber.trim(),
          address: {
            streetAddress: values.address.streetAddress.trim(),
            postalCode: values.address.postalCode.trim(),
            city: values.address.city.trim(),
          },
          billingEmail: values.billingEmail.trim(),
        },
      });
    },
    [createCompany],
  );

  const initialValues: CompanyFormFields = useMemo(() => {
    return {
      name: '',
      address: {
        streetAddress: '',
        postalCode: '',
        city: '',
      },
      registrationNumber: '',
      countryCode: CountryCode.FR,
      vatNumber: '',
      billingEmail: '',
      formattedAddress: '',
      legalForm: '',
    };
  }, []);

  return (
    <Formik
      onSubmit={submit}
      initialValues={initialValues}
      validationSchema={companyFormValidationSchema}
    >
      <CompanyForm mode="create" onClose={onClose} {...rest} />
    </Formik>
  );
};
